import OrderStatuses from '@/enums/OrderStatuses';
import OrderTypes from '@/enums/OrderTypes';
import { userHasRole } from '@/util/RoleUtils';

const {
  INTERNAL_DRAFT,
  DRAFT,
  IN_REVIEW_PENDING,
  IN_REVIEW_REVIEW,
  RETURNED,
  APPROVED_APPROVED,
  APPROVED_PACKAGED,
  APPROVED_COMPLETE,
  HIDDEN,
  ARCHIVED
} = OrderStatuses;

const OrderAccess = {
  isEditable(status, isCRSOwner, isEditing, isAWPackageEditable) {
    if (status === ARCHIVED) {
      return false;
    }
    if (userHasRole('Admin')) {
      return true;
    }
    if (EXTERNAL) {
      switch (status) {
        case DRAFT:
        case RETURNED:
          return true;
        default:
          return false;
      }
    } else {
      switch (status) {
        case INTERNAL_DRAFT:
          return isCRSOwner || userHasRole('Admin');
        case IN_REVIEW_REVIEW:
          return isEditing && (isCRSOwner || userHasRole('Admin'));
        default:
          return isEditing && userHasRole('Admin') && isAWPackageEditable;
      }
    }
  },

  isAttachmentsEditable(status, isEditing, isCRSOwner, isOrderPackageCRSOwner, isOrderPackageEPCOwner) {
    const isAdmin = userHasRole('Admin');

    if (EXTERNAL) {
      switch (status) {
        case DRAFT:
        case RETURNED:
          return true;
        default:
          return false;
      }
    } else {
      switch (status) {
        case INTERNAL_DRAFT:
          return isCRSOwner || isAdmin;
        case IN_REVIEW_REVIEW:
        case APPROVED_APPROVED:
          return isEditing && (isCRSOwner || isAdmin);
        case APPROVED_PACKAGED:
        case APPROVED_COMPLETE:
          return isEditing && (isCRSOwner || isOrderPackageCRSOwner || isOrderPackageEPCOwner || isAdmin);
        default:
          return false;
      }
    }
  },

  isInfoCommEditable(status, isEditing) {
    if (EXTERNAL) return false;

    const isCRS = userHasRole('CRS');
    const isAdmin = userHasRole('Admin');

    switch (status) {
      case IN_REVIEW_PENDING:
      case IN_REVIEW_REVIEW:
      case RETURNED:
      case APPROVED_APPROVED:
      case APPROVED_PACKAGED:
      case APPROVED_COMPLETE:
        return (isCRS || isAdmin) && isEditing;
      default:
        return false;
    }
  },

  isDecisionEditable(status, isCRSOwner) {
    if (!EXTERNAL) {
      switch (status) {
        case IN_REVIEW_REVIEW:
          return isCRSOwner || userHasRole('Admin');
        default:
          return false;
      }
    }
    return false;
  },

  isEditButtonVisible(status, isCRSOwner, isEditing, isAWPackageEditable) {
    if (EXTERNAL || status === INTERNAL_DRAFT) return false;

    return this.isEditable(status, isCRSOwner, !isEditing, isAWPackageEditable);
  },

  isReassignable(status, isCRSOwner, isEditing, isAWPackageEditable) {
    if (EXTERNAL || [INTERNAL_DRAFT, IN_REVIEW_PENDING].includes(status)) return false;

    return this.isEditable(status, isCRSOwner, !isEditing, isAWPackageEditable);
  },

  isPrintable(formType) {
    if (formType === OrderTypes.RELOCATION) {
      return true;
    }
    return false;
  },

  isDeleteable(statusId, externalUserID, userDetails) {
    if (!EXTERNAL) return statusId === INTERNAL_DRAFT;

    switch (statusId) {
      case DRAFT:
        return externalUserID === userDetails.id;
      default:
        false;
    }
  },

  isUnClaimButtonVisible(orderDetails, userDetails) {
    if (EXTERNAL) return false;

    if (
      IN_REVIEW_REVIEW === orderDetails.statusId &&
      (orderDetails.crsOwner === userDetails.id || userHasRole('Admin'))
    ) {
      return true;
    }
    return false;
  },

  isHideButtonVisible(statusId) {
    if (EXTERNAL) return false;
    const isAdmin = userHasRole('Admin');
    if (isAdmin && ![INTERNAL_DRAFT, HIDDEN, ARCHIVED].includes(statusId)) {
      return true;
    } else return false;
  },

  isPriceVisible(orderDetails) {
    if (EXTERNAL && orderDetails.orderFormTypeId === OrderTypes.SPECIALTY_REQ) {
      switch (orderDetails.statusId) {
        case DRAFT:
        case IN_REVIEW_PENDING:
        case IN_REVIEW_REVIEW:
          return false;
        default:
          return true;
      }
    } else {
      return orderDetails.statusId !== INTERNAL_DRAFT;
    }
  },

  isPriceEditable(orderDetails) {
    if (EXTERNAL || orderDetails.statusId === ARCHIVED) {
      return false;
    }
    return this.isPriceVisible(orderDetails);
  },

  isDeliveryScheduleColumnRenderable(status, renderableBy) {
    if (renderableBy === null) return false;

    const showExternal = EXTERNAL || status === INTERNAL_DRAFT;

    if (
      renderableBy === 'All' ||
      (renderableBy === 'Internal' && !showExternal) ||
      (renderableBy === 'External' && showExternal)
    ) {
      return true;
    } else {
      return false;
    }
  },

  isDeliveryScheduleColumnDownloadable(status, downloadableBy) {
    if (downloadableBy === null) return false;

    const showExternal = EXTERNAL || status === INTERNAL_DRAFT;

    if (
      downloadableBy === 'All' ||
      (downloadableBy === 'Internal' && !showExternal) ||
      (downloadableBy === 'External' && showExternal)
    ) {
      return true;
    } else {
      return false;
    }
  }
};

export default OrderAccess;
